import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { Mail, Phone, Send, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
export function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    challenge: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await emailjs.send('service_dg5r25p', 'template_m1n02yq', {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        challenge: formData.challenge
      }, 'jGYM_8TukAYpwySrQ');
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSending(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <section id="contact" className="py-24 md:py-32 bg-secondary border-t border-border" data-id="element-13">
      <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-14">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-15">
          <h2 className="text-xs text-accent tracking-[0.2em] uppercase mb-4" data-id="element-16">
            Get In Touch
          </h2>
          <h3 className="text-4xl md:text-6xl font-serif text-text-light mb-6" data-id="element-17">
            Ready to rethink strategy consulting?
          </h3>
          <p className="text-text-muted text-lg max-w-2xl mx-auto" data-id="element-18">
            Tell us about your challenge and we'll match you with an elite MBA
            team.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-19">
          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:col-span-3" data-id="element-20">
            {submitted ? <div className="bg-white border border-border p-12 text-center" data-id="element-21">
                <CheckCircle className="w-12 h-12 text-accent mx-auto mb-6" data-id="element-22" />
                <h4 className="text-2xl font-serif text-text-light mb-4" data-id="element-23">
                  Challenge Received
                </h4>
                <p className="text-text-muted" data-id="element-24">
                  Thank you for submitting your challenge. Our team will review
                  your brief and be in touch within 24 hours.
                </p>
              </div> : <form onSubmit={handleSubmit} className="bg-white border border-border p-8 md:p-10 space-y-6" data-id="element-25">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-26">
                  <div data-id="element-27">
                    <label htmlFor="name" className="block text-sm font-medium text-text-light mb-2" data-id="element-28">
                      Full Name
                    </label>
                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 bg-white border border-border rounded text-text-light text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors placeholder:text-text-muted/50" placeholder="Your name" data-id="element-29" />
                  </div>
                  <div data-id="element-30">
                    <label htmlFor="email" className="block text-sm font-medium text-text-light mb-2" data-id="element-31">
                      Email Address
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-white border border-border rounded text-text-light text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors placeholder:text-text-muted/50" placeholder="you@company.com" data-id="element-32" />
                  </div>
                </div>

                <div data-id="element-33">
                  <label htmlFor="company" className="block text-sm font-medium text-text-light mb-2" data-id="element-34">
                    Company
                  </label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 bg-white border border-border rounded text-text-light text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors placeholder:text-text-muted/50" placeholder="Your company name" data-id="element-35" />
                </div>

                <div data-id="element-36">
                  <label htmlFor="challenge" className="block text-sm font-medium text-text-light mb-2" data-id="element-37">
                    Describe Your Challenge
                  </label>
                  <textarea id="challenge" name="challenge" required rows={5} value={formData.challenge} onChange={handleChange} className="w-full px-4 py-3 bg-white border border-border rounded text-text-light text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors resize-none placeholder:text-text-muted/50" placeholder="Tell us about the business challenge you'd like our MBA team to tackle..." data-id="element-38" />
                </div>

                {error && <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-id="element-39">
                    <AlertCircle className="w-4 h-4 shrink-0" data-id="element-40" />
                    {error}
                  </div>}

                <button type="submit" disabled={sending} className="w-full bg-tertiary text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-tertiary/90 transition-colors duration-300 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-41">
                  {sending ? <>
                      <Loader2 className="w-4 h-4 animate-spin" data-id="element-42" />
                      Sending...
                    </> : <>
                      <Send className="w-4 h-4" data-id="element-43" />
                      Submit Your Challenge
                    </>}
                </button>
              </form>}
          </motion.div>

          {/* Contact Details */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-2 space-y-8" data-id="element-44">
            <div className="bg-primary border border-border p-8" data-id="element-45">
              <h4 className="font-serif text-xl text-text-light mb-6" data-id="element-46">
                Contact Us Directly
              </h4>

              <div className="space-y-6" data-id="element-47">
                <a href="mailto:challenge@thembacompany.com" className="flex items-start gap-4 group" data-id="element-48">
                  <div className="bg-secondary p-3 rounded-full border border-border group-hover:border-accent transition-colors" data-id="element-49">
                    <Mail className="w-5 h-5 text-accent" data-id="element-50" />
                  </div>
                  <div data-id="element-51">
                    <p className="text-xs text-text-muted uppercase tracking-wider mb-1" data-id="element-52">
                      Email
                    </p>
                    <p className="text-text-light text-sm font-medium group-hover:text-accent transition-colors" data-id="element-53">
                      challenge@thembacompany.com
                    </p>
                  </div>
                </a>

                <a href="tel:+447366319902" className="flex items-start gap-4 group" data-id="element-54">
                  <div className="bg-secondary p-3 rounded-full border border-border group-hover:border-accent transition-colors" data-id="element-55">
                    <Phone className="w-5 h-5 text-accent" data-id="element-56" />
                  </div>
                  <div data-id="element-57">
                    <p className="text-xs text-text-muted uppercase tracking-wider mb-1" data-id="element-58">
                      Telephone
                    </p>
                    <p className="text-text-light text-sm font-medium group-hover:text-accent transition-colors" data-id="element-59">
                      +44 7366 319902
                    </p>
                  </div>
                </a>
              </div>
            </div>

            <div className="bg-primary border border-border p-8" data-id="element-60">
              <h4 className="font-serif text-lg text-text-light mb-3" data-id="element-61">
                What Happens Next?
              </h4>
              <ol className="space-y-4 text-sm text-text-muted" data-id="element-62">
                <li className="flex gap-3" data-id="element-63">
                  <span className="text-accent font-serif font-semibold" data-id="element-64">
                    1.
                  </span>
                  We review your challenge within 24 hours
                </li>
                <li className="flex gap-3" data-id="element-65">
                  <span className="text-accent font-serif font-semibold" data-id="element-66">
                    2.
                  </span>
                  We scope the project and assemble your MBA team
                </li>
                <li className="flex gap-3" data-id="element-67">
                  <span className="text-accent font-serif font-semibold" data-id="element-68">
                    3.
                  </span>
                  Work begins with full Critical Future oversight
                </li>
              </ol>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}